import React, { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Check, X, Loader2, FileText } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminVerifications = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/verifications?status=pending")).data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (row, approve) => {
    const reason = approve ? "" : (window.prompt(`Why is ${row.businessName}'s verification rejected? (emailed to them)`, "") ?? null);
    if (!approve && reason === null) return;
    setBusy(row.id);
    try {
      await api.post(`/admin/verifications/${row.id}/decide`, { approve, reason });
      toast({ title: approve ? "Business verified" : "Verification rejected", description: row.businessName });
      await load();
    } catch { toast({ title: "Action failed" }); }
    finally { setBusy(""); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-verifications">
      <h2 className="font-bold text-foreground flex items-center gap-2"><BadgeCheck size={18} className="text-[#16a34a]" /> Verification requests ({rows.length})</h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">Shops that sent registration documents for the blue tick.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="verifications-empty">Nothing waiting.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="border border-border rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid={`verification-row-${r.id}`}>
              <a href={`${BACKEND_URL}${r.documentUrl}`} target="_blank" rel="noreferrer" className="shrink-0">
                <img src={`${BACKEND_URL}${r.documentUrl}`} alt="document" className="h-16 w-16 rounded-xl object-cover border border-border" />
              </a>
              <div className="flex-1 min-w-[180px]">
                <p className="font-bold text-foreground truncate flex items-center gap-2">
                  {r.businessName}
                  {r.priority && <span className="text-[11px] font-extrabold bg-[#f5b942] text-[#0f2a1a] px-2 py-0.5 rounded-full" data-testid={`verification-priority-${r.id}`}>PRIORITY</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{r.email}{r.registrationNumber ? ` · Reg ${r.registrationNumber}` : ""}</p>
                {r.note && <p className="text-xs text-muted-foreground mt-0.5">{r.note}</p>}
                <a href={`${BACKEND_URL}${r.documentUrl}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#16a34a] hover:underline mt-1">
                  <FileText size={12} /> Open document
                </a>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => decide(r, true)} disabled={busy === r.id} data-testid={`verify-approve-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] text-white rounded-xl px-3 py-2 transition-colors">
                  {busy === r.id ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Approve
                </button>
                <button onClick={() => decide(r, false)} disabled={busy === r.id} data-testid={`verify-reject-${r.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2 transition-colors">
                  <X size={13} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminVerifications;
